/* eslint-disable react/no-unescaped-entities */
import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { resolveErrorMessage } from 'helpers'

// Api
import { sendOTP, verifyOTP } from 'api/otp'

// Hooks
import useMounted from 'use/mounted'
import { useFormHook } from 'use/form'

// Components
import IconPin from 'ui/Icons/IconPin'
import Text from 'ui/Text'
import Row from 'ui/Row'
import Col from 'ui/Col'
import Modal from 'ui/Modal'
import Button from 'ui/Button'
import Input from 'ui/Form/Input'
import Line from 'ui/Line'
import Space from 'ui/Space'
import Loading from 'ui/Loading'

// Validate schema
const validationSchema = yup.object().shape({
  otp: yup.string().trim().required('Please input received OTP code'),
})

export const PopupOTP = ({
  title,
  icon,
  description,
  label,
  placeholder,
  onCancel,
  onConfirm,
  ...props
}) => {
  // State
  const [visible, setVisibleState] = useState(true)
  const [preloading, setPreLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [loading, setLoadingState] = useState(false)
  const [countDown, setCountDown] = useState(60)

  // Form hook
  const isMounted = useMounted()
  const { form, errors, validate, onChange, onBlur, setFieldError } =
    useFormHook({
      schema: validationSchema,
      initialForm: {
        otp: '',
      },
    })

  const setVisible = useCallback(
    (state) => {
      if (isMounted.current) {
        setVisibleState(state)
      }
    },
    [isMounted]
  )

  const setLoading = useCallback(
    (state) => {
      if (isMounted.current) {
        setLoadingState(state)
      }
    },
    [isMounted]
  )

  useEffect(() => {
    let interval = null
    setPreLoading(true)

    sendOTP()
      .then(() => {
        interval = setInterval(() => {
          setCountDown((pre) => {
            if (pre - 1 === 0) {
              clearInterval(interval)
            }
            return pre - 1
          })
        }, 1000)
      })
      .catch(() => {
        //
      })
      .finally(() => {
        setPreLoading(false)
      })

    return () => {
      clearInterval(interval)
    }
  }, [])

  const onResendClick = () => {
    setResending(true)
    setCountDown(60)

    sendOTP(true)
      .then(() => {
        const interval = setInterval(() => {
          setCountDown((pre) => {
            if (pre - 1 === 0) {
              clearInterval(interval)
            }
            return pre - 1
          })
        }, 1000)
      })
      .catch(() => {})
      .finally(() => {
        if (isMounted.current) {
          setResending(false)
        }
      })
  }

  const onCancelClick = () => {
    // Custom callback
    if (onCancel) {
      return onCancel({
        setVisible,
      })
    }

    setVisible(false)
  }

  const onSubmit = async () => {
    try {
      await validate()
    } catch (err) {
      return
    }

    try {
      setLoading(true)
      await verifyOTP(form)

      if (onConfirm) {
        await onConfirm({
          setVisible,
          setLoading,
        })
        return
      }

      setVisible(false)
    } catch (err) {
      setFieldError('otp', await resolveErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const onEnterPress = (e) => {
    if (e.keyCode === 13 && !loading) {
      onSubmit()
    }
  }

  return (
    <Modal show={visible} style={{ textAlign: 'center' }} {...props}>
      <Loading spinning={preloading}>
        <div style={{ textAlign: 'center' }}>{icon}</div>
        <Text size={22} bold paddingBottom={5} color="#000000">
          {title}
        </Text>

        <Text size={16} color="#000000" paddingBottom={15}>
          {description}
        </Text>

        <Row>
          <Col style={{ padding: '0 70px' }}>
            <Input
              name="otp"
              value={form.otp}
              label={label}
              errors={errors}
              placeholder={placeholder}
              style={{ textAlign: 'center' }}
              onChange={onChange}
              onBlur={onBlur}
              onKeyDown={onEnterPress}
            />
          </Col>
        </Row>

        <Line />

        <Space style={{ paddingBottom: 30, justifyContent: 'center' }}>
          <Text size={16} color="#606266">
            Didn't receive the code?
          </Text>
          <Space>
            <Button
              variant="link"
              disabled={countDown > 0}
              onClick={onResendClick}
              loading={resending}
            >
              Resend
            </Button>
          </Space>
          {countDown > 0 && (
            <Text size={16} color="#606266">
              (00:{countDown >= 10 ? countDown : '0' + countDown})
            </Text>
          )}
        </Space>

        <Row>
          <Col xs={6}>
            <Button
              block
              variant="outline-primary"
              disabled={loading}
              onClick={onCancelClick}
            >
              Cancel
            </Button>
          </Col>

          <Col xs={6}>
            <Button block onClick={onSubmit} loading={loading}>
              Submit
            </Button>
          </Col>
        </Row>
      </Loading>
    </Modal>
  )
}

PopupOTP.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  icon: PropTypes.node,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
}

PopupOTP.defaultProps = {
  title: 'Enter One-time PIN',
  description: (
    <Text>
      We sent your OTP to your email address. <br />
      Enter the OTP below to continue:
    </Text>
  ),
  label: 'One-time PIN',
  placeholder: 'Enter PIN here',
  icon: <IconPin />,
  onConfirm: null,
  onCancel: null,
}

export default PopupOTP
